import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, InteractionStatus, InteractionType } from '@azure/msal-browser';
import { combineLatest, defer, merge } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, tap } from 'rxjs/operators';
import { AuthService } from '../auth.service';


interface UserInfo {
  name: string,
  hasAccessToken: boolean
}
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tcc-user-status',
  template: `
<div *ngIf="vm$ | async as vm" class="text-end">
  <div>{{ vm.user.name }}</div>
  <div class="small">
    <ng-container *ngIf="vm.authAction === 'none'; else authAction">
      <button *ngIf="vm.user.hasAccessToken; else noToken"
        class="bg-transparent border-0 p-0 m-0 link-light text-decoration-underline small cursor-pointer" (click)="logout()">SIGN OUT</button>
      <ng-template #noToken>
        <button class="bg-transparent border-0 p-0 m-0 link-light text-decoration-underline small cursor-pointer" (click)="login()">SIGN IN</button>
      </ng-template>
    </ng-container>
    <ng-template #authAction>
      <ng-container *ngIf="vm.authAction === 'login'">Logging In <i class="fa fa-spinner fa-pulse"></i></ng-container>
      <ng-container *ngIf="vm.authAction === 'logout'">Logging Out <i class="fa fa-spinner fa-pulse"></i></ng-container>
    </ng-template>
  </div>
</div>
`
})
export class UserStatusComponent {
  readonly vm$ = defer(() => combineLatest([ this.authAction$, this.userInfo$ ])).pipe(
    map(([authAction, user]) => ({ authAction, user }))
  );

  private readonly authAction$ = this.msalBroadcastSvc.inProgress$.pipe(
    map(x => {
      switch (x) {
        case InteractionStatus.Login: return 'login' as const;
        case InteractionStatus.Logout: return 'logout' as const;
        default: return 'none' as const;
      }
    }),
    startWith('none' as const)
  );
  /** Tries to get initial user info and updates itself after any event. */
  private readonly userInfo$ = merge(
    this.authSvc.authorizedAccount$.pipe(
      startWith(undefined),
      map(x => this.createUserInfo(x))
    )
  );


  constructor(private authSvc: AuthService, private msalBroadcastSvc: MsalBroadcastService) { }

  login() {
    this.authSvc.login().subscribe();
  }

  logout() {
    this.authSvc.logout().subscribe();
  }
  private createUserInfo(user: AccountInfo | undefined): UserInfo {
    return {
      hasAccessToken: !!user,
      name: user
        ? user.name ?? user.username ?? 'Logged In'
        : 'Not logged in'
    };
  }

}
